import React, { useState, useEffect, useRef } from 'react';
import { FiRefreshCw, FiDownload, FiEdit2, FiSave, FiX, FiGlobe, FiUsers, FiDollarSign, FiZap, FiShare2, FiNavigation, FiMonitor, FiSmartphone, FiTablet } from 'react-icons/fi';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' ? '/api/admin' : 'http://localhost:5000/api/admin';

const SOURCE_COLORS = {
  'Paid': '#EF4444', 'Organic Search': '#10B981', 'AI Search': '#8B5CF6',
  'Social': '#F59E0B', 'Email': '#3B82F6', 'Referral': '#06B6D4',
  'Direct': '#6B7280', 'Unknown': '#D1D5DB'
};

const DEVICE_ICONS = { desktop: FiMonitor, mobile: FiSmartphone, tablet: FiTablet };

// CSV export helper
const exportCSV = (data, filename) => {
  if (!data?.length) return;
  const headers = Object.keys(data[0]);
  const rows = [headers.join(','), ...data.map(r => headers.map(h => {
    const v = r[h] ?? '';
    return typeof v === 'string' && v.includes(',') ? `"${v}"` : v;
  }).join(','))];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
};

export default function WebSource() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Ad spend (localStorage)
  const [adSpend, setAdSpend] = useState(() => {
    try { return JSON.parse(localStorage.getItem('rc_ad_spend') || '{}'); } catch { return {}; }
  });
  const [editingSpend, setEditingSpend] = useState(false);
  const [tempSpend, setTempSpend] = useState({});

  const fetchData = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      setError(null);
      const token = localStorage.getItem('rg_token');
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const res = await axios.get(`${API_URL}/analytics/web-source?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) setData(res.data.data);
      else throw new Error(res.data.error);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchData(); }, [startDate, endDate]); // eslint-disable-line

  useEffect(() => {
    if (autoRefresh) {
      setCountdown(30);
      countdownRef.current = setInterval(() => setCountdown(p => p <= 1 ? 30 : p - 1), 1000);
      intervalRef.current = setInterval(() => fetchData(true), 30000);
    }
    return () => { clearInterval(intervalRef.current); clearInterval(countdownRef.current); };
  }, [autoRefresh, startDate, endDate]); // eslint-disable-line

  const getSpend = (p) => adSpend[p] || 0;
  const startEdit = () => { setTempSpend({ ...adSpend }); setEditingSpend(true); };
  const saveSpend = () => { setAdSpend(tempSpend); localStorage.setItem('rc_ad_spend', JSON.stringify(tempSpend)); setEditingSpend(false); };

  const getTypeVisitors = (type) => data?.summary?.byType?.find(t => t.type === type)?.visitors || 0;
  const getTypeConversion = (type) => data?.summary?.byType?.find(t => t.type === type)?.conversionRate || '0.0';

  const platformData = (data?.byPlatform || []).filter(p => p.platform !== 'Unknown').slice(0, 15);

  // Monthly data
  const monthlyMap = {};
  (data?.monthlyTrend || []).forEach(t => {
    if (!monthlyMap[t._id.month]) monthlyMap[t._id.month] = { month: t._id.month, visitors: 0 };
    monthlyMap[t._id.month].visitors += t.count;
  });
  (data?.monthlyBookings || []).forEach(t => {
    if (!monthlyMap[t._id.month]) monthlyMap[t._id.month] = { month: t._id.month, visitors: 0 };
    monthlyMap[t._id.month].bookings = (monthlyMap[t._id.month].bookings || 0) + t.count;
    monthlyMap[t._id.month].revenue = (monthlyMap[t._id.month].revenue || 0) + t.revenue;
  });
  const monthlyData = Object.values(monthlyMap).sort((a, b) => a.month.localeCompare(b.month));

  // Daily trend
  const trendMap = {};
  (data?.trend || []).forEach(t => {
    if (!trendMap[t._id.date]) trendMap[t._id.date] = { date: t._id.date };
    trendMap[t._id.date][t._id.type || 'Unknown'] = t.count;
  });
  const trendData = Object.values(trendMap).sort((a, b) => a.date.localeCompare(b.date));

  const deviceData = (data?.byDevice || []).map(d => ({
    name: d.device === 'unknown' ? 'Other' : d.device.charAt(0).toUpperCase() + d.device.slice(1),
    value: d.count,
    device: d.device
  }));

  if (loading) return <div style={styles.loadingContainer}><div style={styles.spinner} /></div>;

  const totalBookings = (data?.summary?.byType || []).reduce((s, t) => s + (t.bookings || 0), 0);
  const totalRevenue = platformData.reduce((s, p) => s + (p.revenue || 0), 0);
  const overallConv = data?.summary?.totalVisitors > 0 ? ((totalBookings / data.summary.totalVisitors) * 100).toFixed(1) : '0.0';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.headerRow}>
        <div>
          <h2 style={styles.title}>Web Source</h2>
          <p style={styles.subtitle}>All website visitors by traffic source — with conversion rates, ROI & ad spend tracking</p>
        </div>
        <div style={styles.headerActions}>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={styles.dateInput} />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={styles.dateInput} />
          <button onClick={() => fetchData(true)} disabled={refreshing} style={{
            ...styles.refreshBtn,
            opacity: refreshing ? 0.6 : 1
          }}>
            <FiRefreshCw size={14} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          {autoRefresh && <span style={styles.liveChip}>
            <span style={styles.liveDot} />
            Live {countdown}s
          </span>}
          <label style={styles.autoRefreshLabel}>
            <input type="checkbox" checked={autoRefresh} onChange={() => setAutoRefresh(!autoRefresh)} style={styles.autoRefreshCheckbox} />
            Auto-refresh
          </label>
        </div>
      </div>

      {error && <div style={styles.errorAlert}>{error}</div>}

      {/* Summary Cards */}
      <div style={styles.cardGrid6}>
        <StatCard title="Total Visitors" value={(data?.summary?.totalVisitors || 0).toLocaleString()} icon={FiUsers} gradient="linear-gradient(135deg, #1E40AF, #3B82F6)" loading={refreshing} />
        <StatCard title="Total Purchases" value={totalBookings} subtitle={`$${totalRevenue.toFixed(2)} rev · ${overallConv}% conv`} icon={FiDollarSign} gradient="linear-gradient(135deg, #065F46, #10B981)" loading={refreshing} />
        <StatCard title="Paid" value={getTypeVisitors('Paid').toLocaleString()} subtitle={`${getTypeConversion('Paid')}% convert`} icon={FiDollarSign} color="#EF4444" loading={refreshing} />
        <StatCard title="Organic Search" value={getTypeVisitors('Organic Search').toLocaleString()} subtitle={`${getTypeConversion('Organic Search')}% convert`} icon={FiGlobe} color="#10B981" loading={refreshing} />
        <StatCard title="AI Search" value={getTypeVisitors('AI Search').toLocaleString()} subtitle={`${getTypeConversion('AI Search')}% convert`} icon={FiZap} color="#8B5CF6" loading={refreshing} />
        <StatCard title="Direct" value={getTypeVisitors('Direct').toLocaleString()} subtitle={`${getTypeConversion('Direct')}% convert`} icon={FiNavigation} color="#6B7280" loading={refreshing} />
      </div>

      {/* ROI Comparison */}
      <div style={styles.card}>
        <div style={styles.cardHeaderRow}>
          <div>
            <h3 style={styles.cardTitle}>ROI Comparison</h3>
            <p style={styles.cardSubtitle}>
              {editingSpend ? 'Enter your ad spend per platform, then save' : 'Click "Edit Ad Spend" to enter your costs and see CPA, ROAS & profit'}
            </p>
          </div>
          {editingSpend ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={saveSpend} style={styles.btnPrimary}><FiSave size={14} /> Save</button>
              <button onClick={() => setEditingSpend(false)} style={styles.btnOutline}><FiX size={14} /> Cancel</button>
            </div>
          ) : (
            <button onClick={startEdit} style={styles.btnOutline}><FiEdit2 size={14} /> Edit Ad Spend</button>
          )}
        </div>

        {editingSpend && (
          <div style={styles.spendInputGrid}>
            {['Google Ads', 'Meta (Facebook/Instagram)', 'TikTok Ads', 'Microsoft Ads', 'LinkedIn Ads', 'Snapchat Ads', 'Reddit Ads', 'YouTube Ads', 'Pinterest Ads'].map(name => (
              <div key={name} style={styles.spendInputGroup}>
                <label style={styles.spendLabel}>{name}</label>
                <div style={styles.spendInputWrap}>
                  <span style={styles.spendPrefix}>$</span>
                  <input type="number" value={tempSpend[name] || ''} onChange={e => setTempSpend(p => ({ ...p, [name]: parseFloat(e.target.value) || 0 }))} style={styles.spendInput} placeholder="0" />
                </div>
              </div>
            ))}
          </div>
        )}

        {platformData.length > 0 ? (
          <div style={styles.roiGrid}>
            {platformData.map((p, i) => (
              <ROICard key={i} platform={p.platform} type={p.type} visitors={p.visitors} bookings={p.bookings} revenue={p.revenue || 0} adSpend={getSpend(p.platform)} conversionRate={p.conversionRate} />
            ))}
          </div>
        ) : (
          <div style={styles.emptyState}>ROI cards will appear as visitor data comes in.</div>
        )}
      </div>

      {/* Monthly Performance */}
      <div style={styles.card}>
        <div style={styles.cardHeaderRow}>
          <h3 style={styles.cardTitle}>Monthly Performance</h3>
          <button onClick={() => exportCSV(monthlyData.map(m => ({
            Month: m.month, Visitors: m.visitors || 0, Purchases: m.bookings || 0,
            Revenue: `$${(m.revenue || 0).toFixed(2)}`, Conversion: `${m.visitors > 0 ? ((m.bookings || 0) / m.visitors * 100).toFixed(1) : '0.0'}%`
          })), 'monthly_trend')} style={styles.btnText}><FiDownload size={14} /> Export CSV</button>
        </div>
        {monthlyData.length > 0 ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Month</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Visitors</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Purchases</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Revenue</th>
                <th style={{ ...styles.th, width: 200 }}>Conversion</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData.map((m, i) => {
                const conv = m.visitors > 0 ? ((m.bookings || 0) / m.visitors * 100).toFixed(1) : '0.0';
                return (
                  <tr key={i} style={styles.tr}>
                    <td style={styles.td}>{m.month}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{(m.visitors || 0).toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{m.bookings || 0}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${(m.revenue || 0).toFixed(2)}</td>
                    <td style={styles.td}>
                      <div style={styles.barWrap}>
                        <div style={{ ...styles.bar, width: `${Math.min(parseFloat(conv) * 10, 100)}%`, background: parseFloat(conv) >= 3 ? '#10B981' : parseFloat(conv) >= 1 ? '#F59E0B' : '#EF4444' }} />
                        <span style={styles.barLabel}>{conv}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : <div style={styles.emptyState}>Monthly data will appear after more visits</div>}
      </div>

      {/* Platform Performance */}
      <div style={styles.card}>
        <div style={styles.cardHeaderRow}>
          <h3 style={styles.cardTitle}>Platform Performance & Conversion</h3>
          <button onClick={() => exportCSV(platformData.map(p => ({
            Platform: p.platform, Type: p.type, Visitors: p.visitors, Purchases: p.bookings,
            Revenue: `$${(p.revenue || 0).toFixed(2)}`, 'Ad Spend': `$${getSpend(p.platform).toFixed(2)}`,
            ROAS: getSpend(p.platform) > 0 ? `${((p.revenue || 0) / getSpend(p.platform)).toFixed(2)}x` : 'N/A',
            Conversion: `${p.conversionRate}%`
          })), 'platform_performance')} style={styles.btnText}><FiDownload size={14} /> Export CSV</button>
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Platform</th>
              <th style={styles.th}>Type</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Visitors</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Purchases</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Revenue</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Ad Spend</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>ROAS</th>
              <th style={{ ...styles.th, width: 180 }}>Conversion</th>
            </tr>
          </thead>
          <tbody>
            {platformData.length === 0 ? (
              <tr><td colSpan={8} style={{ ...styles.td, textAlign: 'center', padding: 32 }}><span style={{ color: '#9CA3AF' }}>No visitor data yet</span></td></tr>
            ) : platformData.map((p, i) => {
              const spend = getSpend(p.platform);
              const roas = spend > 0 ? ((p.revenue || 0) / spend) : null;
              return (
                <tr key={i} style={styles.tr}>
                  <td style={styles.td}><strong>{p.platform}</strong></td>
                  <td style={styles.td}><span style={{ ...styles.badge, background: `${SOURCE_COLORS[p.type] || '#6B7280'}15`, color: SOURCE_COLORS[p.type] || '#6B7280', border: `1px solid ${SOURCE_COLORS[p.type] || '#6B7280'}30` }}>{p.type}</span></td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{p.visitors.toLocaleString()}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{p.bookings}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>${(p.revenue || 0).toFixed(2)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', color: spend > 0 ? '#111827' : '#D1D5DB' }}>{spend > 0 ? `$${spend.toFixed(0)}` : '-'}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    {roas !== null ? <span style={{ ...styles.badge, background: roas >= 1 ? '#D1FAE5' : '#FEE2E2', color: roas >= 1 ? '#059669' : '#DC2626', fontWeight: 700 }}>{roas.toFixed(1)}x</span> : <span style={{ color: '#D1D5DB' }}>-</span>}
                  </td>
                  <td style={styles.td}>
                    <div style={styles.barWrap}>
                      <div style={{ ...styles.bar, width: `${Math.min(parseFloat(p.conversionRate) * 10, 100)}%`, background: parseFloat(p.conversionRate) >= 3 ? '#10B981' : parseFloat(p.conversionRate) >= 1 ? '#F59E0B' : '#EF4444' }} />
                      <span style={styles.barLabel}>{p.conversionRate}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Top Landing Pages */}
      <div style={styles.card}>
        <div style={styles.cardHeaderRow}>
          <h3 style={styles.cardTitle}>Top Landing Pages</h3>
          <button onClick={() => exportCSV((data?.byLandingPage || []).map(l => ({
            Page: l.landingPage, Visitors: l.visitors, Purchases: l.bookings || 0,
            Revenue: `$${(l.revenue || 0).toFixed(2)}`, Conversion: `${l.conversionRate || '0.0'}%`
          })), 'landing_pages')} style={styles.btnText}><FiDownload size={14} /> Export CSV</button>
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Page</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Visitors</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Purchases</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Revenue</th>
              <th style={{ ...styles.th, width: 180 }}>Conversion</th>
            </tr>
          </thead>
          <tbody>
            {(data?.byLandingPage || []).length === 0 ? (
              <tr><td colSpan={5} style={{ ...styles.td, textAlign: 'center', padding: 24 }}><span style={{ color: '#9CA3AF' }}>No data yet</span></td></tr>
            ) : (data.byLandingPage || []).map((l, i) => (
              <tr key={i} style={styles.tr}>
                <td style={styles.td}><code style={styles.code}>{l.landingPage}</code></td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{l.visitors.toLocaleString()}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{l.bookings || 0}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>${(l.revenue || 0).toFixed(2)}</td>
                <td style={styles.td}>
                  <div style={styles.barWrap}>
                    <div style={{ ...styles.bar, width: `${Math.min(parseFloat(l.conversionRate || 0) * 10, 100)}%`, background: parseFloat(l.conversionRate) >= 3 ? '#10B981' : parseFloat(l.conversionRate) >= 1 ? '#F59E0B' : '#EF4444' }} />
                    <span style={styles.barLabel}>{l.conversionRate || '0.0'}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Devices & Browsers */}
      <div style={styles.twoCol}>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Devices</h3>
          {deviceData.length > 0 ? deviceData.map((d, i) => {
            const pct = data?.summary?.totalVisitors > 0 ? (d.value / data.summary.totalVisitors * 100) : 0;
            const Icon = DEVICE_ICONS[d.device] || FiMonitor;
            return (
              <div key={i} style={styles.deviceRow}>
                <Icon size={14} style={{ color: '#6B7280', flexShrink: 0 }} />
                <span style={styles.deviceLabel}>{d.name}</span>
                <div style={styles.deviceBarOuter}>
                  <div style={{ ...styles.deviceBar, width: `${pct}%`, background: d.device === 'desktop' ? '#3B82F6' : d.device === 'mobile' ? '#10B981' : '#F59E0B' }} />
                </div>
                <span style={styles.deviceValue}>{d.value.toLocaleString()} ({pct.toFixed(0)}%)</span>
              </div>
            );
          }) : <div style={styles.emptyState}>No data yet</div>}
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Browsers</h3>
          {(data?.byBrowser || []).length > 0 ? (data.byBrowser).map((b, i) => {
            const pct = data?.summary?.totalVisitors > 0 ? (b.count / data.summary.totalVisitors * 100) : 0;
            return (
              <div key={i} style={styles.deviceRow}>
                <span style={styles.deviceLabel}>{b.browser || 'Other'}</span>
                <div style={styles.deviceBarOuter}>
                  <div style={{ ...styles.deviceBar, width: `${pct}%`, background: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4'][i % 6] }} />
                </div>
                <span style={styles.deviceValue}>{b.count.toLocaleString()}</span>
              </div>
            );
          }) : <div style={styles.emptyState}>No data yet</div>}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Stat Card Component
// ============================================
function StatCard({ title, value, subtitle, icon: Icon, color, gradient, loading }) {
  return (
    <div style={{ ...styles.statCard, background: gradient || 'var(--bg-secondary)', border: gradient ? 'none' : '1px solid var(--border)' }}>
      <div style={styles.statCardInner}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: gradient ? 'rgba(255,255,255,0.8)' : 'var(--text-secondary)', marginBottom: 4 }}>{title}</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: gradient ? 'white' : 'var(--text-primary)' }}>
            {loading ? '...' : value}
          </div>
          {subtitle && <div style={{ fontSize: 11, color: gradient ? 'rgba(255,255,255,0.7)' : 'var(--text-secondary)', marginTop: 2 }}>{subtitle}</div>}
        </div>
        <div style={{ width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: gradient ? 'rgba(255,255,255,0.2)' : `${color}15` }}>
          <Icon size={22} style={{ color: gradient ? 'white' : color }} />
        </div>
      </div>
    </div>
  );
}

// ============================================
// ROI Card Component
// ============================================
function ROICard({ platform, type, visitors, bookings, revenue, adSpend, conversionRate }) {
  const cpa = bookings > 0 ? (adSpend / bookings) : 0;
  const roas = adSpend > 0 ? (revenue / adSpend) : 0;
  const profit = revenue - adSpend;
  const isFree = adSpend === 0 && bookings > 0;
  const isProfitable = profit > 0 || isFree;

  return (
    <div style={{
      ...styles.roiCard,
      border: `1px solid ${isProfitable ? '#D1FAE5' : profit === 0 && !isFree ? 'var(--border)' : '#FEE2E2'}`,
      background: isProfitable ? '#F0FDF410' : profit === 0 && !isFree ? 'var(--bg-secondary)' : '#FEF2F210'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{platform}</div>
          <span style={{ ...styles.badge, background: `${SOURCE_COLORS[type] || '#6B7280'}15`, color: SOURCE_COLORS[type] || '#6B7280', fontSize: 10, marginTop: 4, display: 'inline-block' }}>{type}</span>
        </div>
        {isFree ? (
          <span style={{ ...styles.badge, background: '#D1FAE5', color: '#059669', fontWeight: 700 }}>FREE</span>
        ) : adSpend > 0 ? (
          <span style={{ ...styles.badge, background: isProfitable ? '#D1FAE5' : '#FEE2E2', color: isProfitable ? '#059669' : '#DC2626', fontWeight: 700 }}>{roas.toFixed(1)}x ROAS</span>
        ) : null}
      </div>
      <div style={styles.roiStats}>
        <div><div style={styles.roiLabel}>Visitors</div><div style={styles.roiValue}>{visitors.toLocaleString()}</div></div>
        <div><div style={styles.roiLabel}>Purchases</div><div style={styles.roiValue}>{bookings}</div></div>
        <div><div style={styles.roiLabel}>Revenue</div><div style={styles.roiValue}>${revenue.toFixed(0)}</div></div>
      </div>
      {adSpend > 0 && (
        <div style={{ ...styles.roiStats, marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          <div><div style={styles.roiLabel}>Ad Spend</div><div style={styles.roiValue}>${adSpend.toFixed(0)}</div></div>
          <div><div style={styles.roiLabel}>CPA</div><div style={styles.roiValue}>${cpa.toFixed(2)}</div></div>
          <div><div style={styles.roiLabel}>Profit</div><div style={{ ...styles.roiValue, color: profit >= 0 ? '#059669' : '#DC2626' }}>{profit >= 0 ? '+' : ''}${profit.toFixed(0)}</div></div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Styles
// ============================================
const styles = {
  container: { maxWidth: 1400, margin: '0 auto' },
  loadingContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' },
  spinner: { width: 32, height: 32, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 16 },
  title: { fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', margin: 0 },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0' },
  headerActions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  dateInput: { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13, width: 150 },
  refreshBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  liveChip: { padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, background: '#ECFDF5', color: '#059669', border: '1px solid #A7F3D0' },
  liveDot: { width: 8, height: 8, borderRadius: '50%', background: '#10B981', animation: 'pulse 2s infinite' },
  autoRefreshLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' },
  autoRefreshCheckbox: { width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer' },
  errorAlert: { padding: '10px 16px', background: '#FEE2E2', color: '#DC2626', borderRadius: 8, marginBottom: 16, fontSize: 13 },

  cardGrid6: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 },
  statCard: { borderRadius: 12, padding: 20, transition: 'transform 0.2s' },
  statCardInner: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },

  card: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 20 },
  cardHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 },
  cardTitle: { fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: 0 },
  cardSubtitle: { fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' },

  btnPrimary: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: '#10B981', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnOutline: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  btnText: { display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' },

  spendInputGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, padding: 16, background: 'var(--bg-primary)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 16 },
  spendInputGroup: {},
  spendLabel: { fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 },
  spendInputWrap: { display: 'flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' },
  spendPrefix: { padding: '4px 8px', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', fontSize: 13, borderRight: '1px solid var(--border)' },
  spendInput: { border: 'none', padding: '6px 8px', fontSize: 13, width: '100%', background: 'var(--bg-secondary)', color: 'var(--text-primary)', outline: 'none' },

  roiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 },
  roiCard: { borderRadius: 10, padding: 16, transition: 'transform 0.2s' },
  roiStats: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 },
  roiLabel: { fontSize: 11, color: 'var(--text-secondary)' },
  roiValue: { fontSize: 14, fontWeight: 600 },

  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },

  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' },
  tr: { borderBottom: '1px solid var(--border)' },
  td: { padding: '10px 12px', fontSize: 13 },
  code: { fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' },

  barWrap: { display: 'flex', alignItems: 'center', gap: 8 },
  bar: { height: 6, borderRadius: 3, transition: 'width 0.3s' },
  barLabel: { fontSize: 12, fontWeight: 600, minWidth: 40, textAlign: 'right' },

  twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 20 },
  deviceRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  deviceLabel: { fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500, minWidth: 70 },
  deviceBarOuter: { flex: 1, height: 8, borderRadius: 4, background: 'var(--bg-primary)' },
  deviceBar: { height: '100%', borderRadius: 4, transition: 'width 0.3s' },
  deviceValue: { fontSize: 13, fontWeight: 600, minWidth: 80, textAlign: 'right' },

  emptyState: { textAlign: 'center', padding: 32, color: 'var(--text-secondary)', fontSize: 13 }
};
